/**
 * DataViewer utility functions.
 */

export function normalizeTags(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t).trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s) as unknown;
        return Array.isArray(parsed) ? parsed.map((t) => String(t).trim()).filter(Boolean) : [];
      } catch {
        return [];
      }
    }
    if (s.startsWith('{') && s.endsWith('}')) {
      return s
        .slice(1, -1)
        .split(',')
        .map((t) => t.trim().replace(/^"|"$/g, ''))
        .filter(Boolean);
    }
    return s ? [s] : [];
  }
  return [];
}

/** Sentinel in species filter chip lists meaning the row has no species set. */
export const SPECIES_NONE_TOKEN = '__no_species__';

export function speciesFilterChipLabel(value: string): string {
  return value === SPECIES_NONE_TOKEN ? '(no species)' : value;
}

/**
 * Species filter: optional include list (OR), optional exclude list.
 * Empty include = do not restrict by species. Excludes always apply first.
 */
export function matchesSpeciesFilters(
  species: string | null | undefined,
  includeSpecies: string[],
  excludeSpecies: string[]
): boolean {
  const norm = String(species ?? '')
    .trim()
    .toLowerCase();
  const isEmpty = !norm;

  for (const ex of excludeSpecies) {
    if (ex === SPECIES_NONE_TOKEN) {
      if (isEmpty) return false;
    } else if (norm === String(ex).trim().toLowerCase()) {
      return false;
    }
  }

  if (includeSpecies.length === 0) {
    return true;
  }

    return includeSpecies.some((inc) => {
    if (inc === SPECIES_NONE_TOKEN) return isEmpty;
    return norm === String(inc).trim().toLowerCase();
  });
}

export type MetricsMap = Record<string, unknown>;

export function normalizeMetrics(raw: unknown): MetricsMap {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const out: MetricsMap = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (k && v != null && String(v).trim() !== '') {
      out[k] = v;
    }
  }
  return out;
}

/** Assessment with optional per-camera S3 URLs (NERA viewer). */
export interface NeraAssessmentUrls {
  right_url?: string | null;
  left_url?: string | null;
  bottom_url?: string | null;
}

const NERA_CAMERA_URL_KEYS = ['right_url', 'left_url', 'bottom_url'] as const;

/** Merge metrics from all camera images in one assessment (first value wins per key). */
export function getAssessmentMetrics(
  assessment: NeraAssessmentUrls,
  metricsByUrl: Record<string, MetricsMap>
): MetricsMap {
  const merged: MetricsMap = {};
  for (const key of NERA_CAMERA_URL_KEYS) {
    const url = assessment[key];
    if (!url) continue;
    const metrics = metricsByUrl[url];
    if (!metrics) continue;
    for (const [k, v] of Object.entries(metrics)) {
      if (!(k in merged)) {
        merged[k] = v;
      }
    }
  }
  return merged;
}

export function assessmentMetricsReady(
  assessment: NeraAssessmentUrls,
  metricsByUrl: Record<string, MetricsMap>
): boolean {
  const urls = NERA_CAMERA_URL_KEYS.map((k) => assessment[k]).filter(Boolean) as string[];
  if (urls.length === 0) return true;
  return urls.every((u) => metricsByUrl[u] !== undefined);
}

/** Filter/suggestion token for a metric key-value pair (e.g. manual_quality:good). */
export function metricToFilterToken(key: string, value: unknown): string {
  return `${key}:${String(value).trim()}`;
}

export function metricsToFilterTokens(metrics: MetricsMap): string[] {
  return Object.entries(metrics).map(([k, v]) => metricToFilterToken(k, v));
}

export function getAssessmentTagUnion(
  assessment: NeraAssessmentUrls,
  tagsByUrl: Record<string, string[]>
): string[] {
  const set = new Set<string>();
  for (const key of NERA_CAMERA_URL_KEYS) {
    const url = assessment[key];
    if (url) {
      normalizeTags(tagsByUrl[url]).forEach((t) => set.add(t));
    }
  }
  return Array.from(set);
}

/** Union of tags and metric filter tokens across all camera images in an assessment. */
export function getAssessmentFilterUnion(
  assessment: NeraAssessmentUrls,
  tagsByUrl: Record<string, string[]>,
  metricsByUrl: Record<string, MetricsMap>
): string[] {
  const set = new Set<string>();
  for (const key of NERA_CAMERA_URL_KEYS) {
    const url = assessment[key];
    if (!url) continue;
    normalizeTags(tagsByUrl[url]).forEach((t) => set.add(t));
    metricsToFilterTokens(normalizeMetrics(metricsByUrl[url])).forEach((t) => set.add(t));
  }
  return Array.from(set);
}

/** Include filter: OR across groups, AND within each group. Empty groups ignored. */
export function matchesIncludeGroups(tags: string[], includeGroups: string[][]): boolean {
  const normTags = tags.map((t) => t.trim().toLowerCase());
  const nonEmpty = includeGroups
    .map((g) => g.map((t) => t.trim().toLowerCase()).filter(Boolean))
    .filter((g) => g.length > 0);
  if (nonEmpty.length === 0) return true;
  return nonEmpty.some((group) => group.every((tag) => normTags.includes(tag)));
}

/** Exclude filter: hide if any excluded token is present. */
export function matchesExcludeTags(tags: string[], excludeTags: string[]): boolean {
  if (excludeTags.length === 0) return true;
  const normTags = tags.map((t) => t.trim().toLowerCase());
  return !excludeTags.some((t) => normTags.includes(t.trim().toLowerCase()));
}
